import { styled } from "@mui/system";
import { colors } from "../../../utils";
import { shadow } from "../../../theme";
import { accentBorderSx } from "../../Theming/themeEngine";


export const SwitchContainer = styled("div")(({ disabled }) => ({
  height: 30,
  minWidth: 90,
  display: "flex",
  borderRadius: 100,
  gap: 4,
  backgroundColor: colors.background,
  opacity: disabled ? 0.5 : 1,
  pointerEvents: disabled ? "none" : "auto",
  marginLeft:10
}));

export const SwitchButton = styled("div")(({ selected, disabled }) => ({
  width: selected ? 55 : 28,
  borderRadius: 100,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor:
    disabled && selected
      ? colors.lightBorder
      : selected
      ? `var(--gt-accent, ${colors.PrimaryPurple500})`
      : "transparent",
  cursor: disabled ? "not-allowed" : "pointer",
  padding: selected ? 4 : 0,
}));

// gordon-0921-WEB-gallery-theme-hover-and-selected-highlights-must-use-the-
// theme-accent-colour-sweep: the active view-toggle pill (SwitchButton above)
// is a SOLID accent fill, so its label reads --gt-accent-on-fill-text (WCAG
// AA 4.5:1 against the accent itself via the shared D-11 autoTextColor
// helper), not a hardcoded "white".
export const SwitchText = styled("span")(({ disabled }) => ({
  fontSize: 12,
  color: disabled ? colors.lightGrey : "var(--gt-accent-on-fill-text, white)",
  paddingLeft: 4,
}));

export const EmptyWheelsContainer = styled("div")(({ height = "38" }) => ({
  background: `linear-gradient(45deg, #F7FED5, #CCFEFF, #FDCFFE, #F5EEDE)`,
  height: `${height}vh`,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexDirection: "column",
  gap:20
}));

export const CardContainer = styled("div")({
  height: "32vh",
  padding: 10,
  // gordon-0913-gallery-theme-panel-background-and-accent-card-border (b):
  // follows the Gallery Theme "Card corners" radius (--gt-card-radius, already
  // emitted by pages/Gallery/index.jsx for the profile/primary-wheel cards);
  // unset keeps today's hardcoded 8px, byte-identical. Every wheel card also
  // gets a 1px solid border in the theme's Accent colour — UNIVERSAL (unlike
  // decision #gallery-card-white for the fill above): even an untouched
  // default-theme gallery gets --gt-card-border (pages/Gallery/index.jsx
  // always emits it, falling back to DEFAULT_ACCENT). gordon-0918b-gallery-
  // accent-border-all-four-spaces: shared accentBorderSx (Theming/
  // themeEngine.js) so this and the Business Box / Profile / Featured Wheel /
  // Wheel Grid wrappers can never diverge. Thickens to 2px on hover below,
  // same colour — never recolours.
  ...accentBorderSx(),
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  boxSizing: "border-box",
  // BUG FIX (ISP-9999, "gallery-card-white-REDRIVE-theme-set"): this card
  // used to read `var(--gt-surface, ${colors.white})`, so a creator-set
  // Gallery Theme "Background" colour (--gt-surface, inherited from the
  // themed ancestor Grid in pages/Gallery/index.jsx) tinted the card FRAME
  // itself -- confirmed live on web-stg: a lavender bg_color (#E6D7F5)
  // theme painted CardContainer that exact lavender instead of white.
  // Decision #gallery-card-white: the frame stays pure white on every
  // platform regardless of Gallery Theme; only the PAGE background (galleryBg
  // in pages/Gallery/index.jsx) is meant to carry the theme. Matches its
  // Discover counterpart (ExploreV2/ExploreWheelCard.jsx's `Card` --
  // `background: ${colors.white}`, never themed) and the Gallery Theme
  // studio's own live preview (GalleryThemePreview.jsx's wheel-card mock is
  // hardcoded `background: "#fff"`, never the resolved surface).
  backgroundColor: colors.white,
  // ISP-9999 #937: same on-hover shadow as the Discover card
  // (ExploreV2/ExploreWheelCard.jsx's `Card` -- `&:hover { box-shadow: shadow.e3 }`)
  // so a Gallery Wheel Card raises identically on hover.
  transition: "box-shadow 0.18s ease, border-width 0.12s ease",
  "&:hover": {
    boxShadow: shadow.e3,
    // thicken only, never recolour (border-width is a standalone longhand —
    // it composes with the `border` shorthand above without touching colour).
    borderWidth: 2,
  },
});

export const CardHeaderContainer = styled("div")({
  flexShrink: 0,
  maxHeight: "24%",
  display: "flex",
  justifyContent: "space-between",
  textOverflow: "ellipsis",
});

/** Absolute wallpaper layer (hue ::after must not sit on the same stacking context as the wheel). */
export const CardWallpaperBackground = styled("div")(
  ({ bg, wallpaperHueColor, wallpaperHuePercentage }) => {
    let background = "#F6F7F9";
    const isImageUrl = (value) => /^(http|https|data):/.test(value);

    if (isImageUrl(bg)) {
      background = `url(${bg}) center/cover no-repeat`;
    } else if (bg) {
      background = bg;
    }

    return {
      position: "absolute",
      inset: 0,
      borderRadius: 4,
      zIndex: 0,
      background,
      "&::after": {
        content: '""',
        position: "absolute",
        inset: 0,
        backgroundColor: wallpaperHueColor ?? "#ffffff",
        opacity: ((wallpaperHuePercentage ?? 0) / 100),
        mixBlendMode: "multiply",
        pointerEvents: "none",
        borderRadius: 4,
      },
    };
  }
);

export const CardImageContainer = styled("div")({
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: 4,
  flexGrow: 1,
  maxHeight: "19vh",
  overflow: "hidden",
  position: "relative",
});

export const TabsWrapper = styled("div")({
  position: "sticky",
  top: 0,
  zIndex: 1000,
  backgroundColor: "#FFFFFF",
});

export const GradientContainer = styled('div')({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: '100%',
  height: '100%',
  background: `
    conic-gradient(
      from 180deg at 50% 50%, 
      #F5EEDE -41.73deg, 
      #CCFEFF 37.8deg, 
      #F7FED5 140.4deg, 
      #FDCFFE 216deg, 
      #F5EEDE 318.27deg, 
      #CCFEFF 397.8deg
    ),
    linear-gradient(
      0deg, 
      rgba(255, 255, 255, 0.5), 
      rgba(255, 255, 255, 0.5)
    )
  `,
  backgroundBlendMode: 'overlay',
});

export const NoDataContainer = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "382px",
  flexDirection: "column",
  backgroundColor: "#fff",
  padding: "24px",
  gap: "24px",
  borderRadius: "8px",
  boxShadow: "0px 0px 9.3px 0px #3B3B3B1C"
});

export const IconContainer = styled("div")({
  position: "relative",
});

export const TextContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "16px",
});

export const StyledText = styled("span")({
  fontSize: "16px",
  fontWeight: 700,
  color: "#123051",
  lineHeight: "20px",
  textAlign: "center",
  fontFamily: "DM Sans",
});